/**
 * SUPERBA SEARCH — results page (/search?q=)
 * ---------------------------------------------------------------------------
 * Renders /search from assets/search-index.json instead of Shopify's native
 * results. The reason is duplication, not tidiness: every colourway is its own
 * product, so Shopify returns ~907 rows for "marfa" where the index holds 29
 * pieces. Filtering the £0.00 swatch products out of the native page only takes
 * 907 to 903 — the swatches were never the problem.
 *
 * The engine itself lives in assets/superba-search.js and is shared through
 * window.SuperbaSearch, so the dropdown and this page cannot disagree about what
 * matches, how pieces group, or how a card looks.
 *
 * Progressive enhancement: snippets/search-results.liquid hides Shopify's own
 * results the moment JS runs, and this file puts them back if anything here
 * fails. A broken index means today's page, never a blank one.
 */
(function () {
  'use strict';

  var root = document.querySelector('[data-superba-search-page]');
  if (!root) return;

  /* Put Shopify's results back and stand down. */
  function bail() {
    document.documentElement.classList.remove('sp-js');
    root.setAttribute('hidden', '');
  }

  var API = window.SuperbaSearch;
  if (!API) return bail();

  var SEARCH_URL = root.dataset.searchUrl || '/search';
  var rail = root.querySelector('.sp__rail');
  var head = root.querySelector('.sp__head');
  var grid = root.querySelector('.sp__grid');
  var field = root.querySelector('.sp__q');
  if (!rail || !head || !grid) return bail();

  /* Storefront rejects very large aliased queries — 8 cards of up to 6 covers is
     ~48 aliases a request, well inside what it accepts. */
  var CHUNK = 8;

  var query = '';
  var all = [];                 /* every piece matching the query */
  var state = { range: null, cat: null, cover: null, sort: 'size' };

  /* ------------------------------------------------------------- covers -- */

  /* Cover families are the first word of a cover name — Marfa, Momo, Felix.
     It is how people actually shop fabric ("have you got it in Marfa?") and the
     same grouping the dropdown's cover chips use. */
  function family(name) {
    return String(name).split(/\s+/)[0];
  }

  function coverIn(row, fam) {
    var want = fam.toLowerCase();
    for (var i = 0; i < row.c.length; i++) {
      if (family(row.c[i][0]).toLowerCase() === want) return row.c[i];
    }
    return null;
  }

  /* ------------------------------------------------------------ filters -- */

  /* `skip` leaves one dimension out, so that dimension's own counts are of
     everything the OTHER filters allow. Without it, picking Edgar would leave
     "Sofas 8" in the rail while clicking it returned nothing. */
  function passes(row, skip) {
    if (skip !== 'range' && state.range && row.r !== state.range) return false;
    if (skip !== 'cat' && state.cat && API.category(row) !== state.cat) return false;
    if (skip !== 'cover' && state.cover && !coverIn(row, state.cover)) return false;
    return true;
  }

  function subset(skip) {
    return all.filter(function (r) { return passes(r, skip); });
  }

  function countBy(list, fn) {
    var m = {};
    list.forEach(function (r) {
      var k = fn(r);
      if (k && k !== 'Other') m[k] = (m[k] || 0) + 1;
    });
    return Object.keys(m)
      .sort(function (a, b) { return m[b] - m[a] || a.localeCompare(b); })
      .map(function (k) { return { label: k, count: m[k] }; });
  }

  /* A piece counts once per family however many covers of it that family has. */
  function countCovers(list) {
    var m = {};
    list.forEach(function (r) {
      var seen = {};
      (r.c || []).forEach(function (c) {
        var f = family(c[0]);
        if (!f || seen[f]) return;
        seen[f] = 1;
        m[f] = (m[f] || 0) + 1;
      });
    });
    return Object.keys(m)
      .sort(function (a, b) { return m[b] - m[a] || a.localeCompare(b); })
      .map(function (k) { return { label: k, count: m[k] }; });
  }

  /* ------------------------------------------------------------- render -- */

  function group(title, kind, items) {
    if (!items.length) return '';
    return '<div class="sp__rg"><div class="sp__rt">' + title + '</div>' +
      items.map(function (x) {
        var on = state[kind] === x.label;
        return '<button type="button" class="sp__ri' + (on ? ' is-on' : '') + '"' +
          ' data-kind="' + kind + '" data-value="' + API.esc(x.label) + '">' +
          '<span>' + API.esc(x.label) + '</span>' +
          '<span class="sp__rn">' + x.count + '</span></button>';
      }).join('') + '</div>';
  }

  /* Each row carries the cover it should be shown in: the one the query named,
     or the one the cover filter asked for. Photo, price and the ringed swatch
     all follow from it — the same mechanism the dropdown uses. */
  function resultsFor(list) {
    return list.map(function (row) {
      var cover = state.cover ? coverIn(row, state.cover) : API.matchedCover(row, query);
      return { row: row, cover: cover };
    });
  }

  function sortResults(results) {
    var s = state.sort;
    if (s === 'asc' || s === 'desc') {
      return results.slice().sort(function (a, b) {
        var pa = a.cover ? a.cover[2] : a.row.p;
        var pb = b.cover ? b.cover[2] : b.row.p;
        return s === 'asc' ? pa - pb : pb - pa;
      });
    }
    if (s === 'az') {
      return results.slice().sort(function (a, b) { return a.row.t.localeCompare(b.row.t); });
    }
    return results;   /* 'size' — already ordered largest first by the engine */
  }

  function activeCount() {
    return (state.range ? 1 : 0) + (state.cat ? 1 : 0) + (state.cover ? 1 : 0);
  }

  /* Before anyone types, /search shows what the dropdown shows: the live
     collections shelf from /pages/collections. Clicking a category in the rail
     turns it into a real listing of everything in that category — so the page is
     browsable without a query, rather than an empty box. */
  function isShelf() {
    return !query && !activeCount() && API.collections() && API.collections().length;
  }

  function draw() {
    var shelf = isShelf();
    var list = subset(null);
    var results = shelf ? [] : sortResults(resultsFor(list));

    /* No query means no relevance order to sort by, and nothing to narrow by
       collection when the collections themselves are the results. */
    rail.innerHTML =
      (query ? group('Collections', 'range', countBy(subset('range'), function (r) { return r.r; })) : '') +
      group(query ? 'Category' : 'Shop by', 'cat', countBy(subset('cat'), API.category)) +
      group('Cover', 'cover', countCovers(subset('cover')).slice(0, 12)) +
      (activeCount() ? '<button type="button" class="sp__clear">Clear ' +
        (activeCount() > 1 ? 'filters' : 'filter') + '</button>' : '');

    var ranges = {};
    list.forEach(function (r) { if (r.r) ranges[r.r] = 1; });
    var nr = Object.keys(ranges).length;

    var title, count;
    if (shelf) {
      title = 'Shop by collection';
      count = API.collections().length + ' collections &middot; ' + list.length + ' pieces';
    } else if (!query) {
      title = API.esc(state.cat || state.cover || state.range);
      count = list.length + (list.length === 1 ? ' piece' : ' pieces');
    } else {
      title = state.cover
        ? 'Pieces in <em>' + API.esc(state.cover) + '</em>'
        : 'Results for <em>' + API.esc(query) + '</em>';
      count = list.length + (list.length === 1 ? ' piece' : ' pieces') +
        (nr ? ' &middot; ' + nr + (nr === 1 ? ' collection' : ' collections') : '');
    }

    head.innerHTML =
      '<div><h1 class="sp__h1">' + title + '</h1>' +
      '<p class="sp__count">' + count + '</p></div>' +
      (shelf ? '' :
        '<select class="sp__sort" aria-label="Sort results">' +
          '<option value="size">Sort: Largest first</option>' +
          '<option value="asc">Price: low to high</option>' +
          '<option value="desc">Price: high to low</option>' +
          '<option value="az">Name: A–Z</option>' +
        '</select>');
    if (!shelf) head.querySelector('.sp__sort').value = state.sort;

    if (shelf) {
      grid.className = 'sp__grid is-shelf';
      grid.innerHTML = API.collections().map(API.collectionHTML).join('');
      return;
    }
    grid.className = 'sp__grid';

    if (!query && !list.length) {
      grid.innerHTML = API.emptyHTML('Nothing in that filter', 'Clear it to see the rest of the collection.');
      return;
    }

    if (!results.length) {
      grid.innerHTML = all.length
        ? API.emptyHTML('Nothing matches those filters', 'Clear one to widen the search.')
        : API.emptyHTML('Nothing matches \u201c' + API.esc(query) + '\u201d',
            'Try a collection like Oli, a piece like corner sofa, or a cover like Marfa.');
      return;
    }

    grid.innerHTML = results.map(API.cardHTML).join('');
    for (var i = 0; i < results.length; i += CHUNK) {
      API.hydrate(results.slice(i, i + CHUNK), grid);
    }
  }

  /* ---------------------------------------------------------------- URL -- */

  /* Filters live in the URL so a filtered page can be shared, survives a
     refresh, and the back button steps out of a filter rather than off the
     site. */
  function readURL() {
    var p = new URLSearchParams(location.search);
    query = (p.get('q') || '').trim();
    state.range = p.get('collection');
    state.cat = p.get('category');
    state.cover = p.get('cover');
    state.sort = p.get('sort') || 'size';
  }

  function writeURL(push) {
    var p = new URLSearchParams();
    if (query) p.set('q', query);
    if (state.range) p.set('collection', state.range);
    if (state.cat) p.set('category', state.cat);
    if (state.cover) p.set('cover', state.cover);
    if (state.sort !== 'size') p.set('sort', state.sort);
    var url = SEARCH_URL + '?' + p.toString();
    if (push) history.pushState(null, '', url);
    else history.replaceState(null, '', url);
  }

  /* -------------------------------------------------------------- events -- */

  root.addEventListener('click', function (e) {
    if (e.target.closest('.sp__clear')) {
      state.range = state.cat = state.cover = null;
      writeURL(true);
      return draw();
    }
    var item = e.target.closest('.sp__ri');
    if (!item) return;
    var kind = item.dataset.kind, value = item.dataset.value;
    state[kind] = state[kind] === value ? null : value;   /* click again to clear */
    writeURL(true);
    draw();
  });

  root.addEventListener('change', function (e) {
    if (!e.target.closest('.sp__sort')) return;
    state.sort = e.target.value;
    writeURL(true);
    draw();
  });

  /* Editing the query re-runs it here rather than reloading the page. Filters
     reset on a new query — the same rule the dropdown uses, since a filter from
     the last search rarely makes sense for the next one. */
  var typing = null;
  if (field) {
    field.addEventListener('input', function () {
      clearTimeout(typing);
      typing = setTimeout(function () {
        var next = field.value.trim();
        if (next === query) return;
        query = next;
        state.range = state.cat = state.cover = null;
        all = query ? API.search(query).all : API.rows().slice().sort(API.bySize);
        writeURL(false);   /* replace, not push — typing should not fill history */
        draw();
      }, 140);
    });
    /* Enter would otherwise reload the page we have already rendered. */
    root.addEventListener('submit', function (e) {
      e.preventDefault();
      clearTimeout(typing);
      var next = field.value.trim();
      if (next !== query) {
        query = next;
        state.range = state.cat = state.cover = null;
        all = query ? API.search(query).all : API.rows().slice().sort(API.bySize);
      }
      writeURL(true);
      draw();
      field.blur();
    });
  }

  window.addEventListener('popstate', function () {
    readURL();
    all = query ? API.search(query).all : API.rows().slice().sort(API.bySize);
    if (field) field.value = query;
    draw();
  });

  /* ---------------------------------------------------------------- boot -- */

  readURL();

  /* The shelf needs /pages/collections as well as the index. Both at once, and
     a failed shelf is not fatal — without it an empty query just lists the
     catalogue by category. */
  Promise.all([API.load(), API.loadCollections()])
    .then(function () {
      all = query ? API.search(query).all : API.rows().slice().sort(API.bySize);
      if (field) field.value = query;
      root.removeAttribute('hidden');
      draw();
    })
    .catch(function (err) {
      console.warn('Superba search page: index failed, falling back', err);
      bail();
    });
})();
